import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';

import { CompanySwitchStrategy } from '@core/http-legacy';
import { writeOrgGuidToLocalStorage } from '@core/tellent-client-logic';
import { TellentCurrentUserSelectors } from '@core/tellent-data';
import { injectIsNativeMobileHr, switchOrgInNativeApp } from '@core/tellent-mobile-contracts';
import { AppKind, injectTellentAppKind, SeatKind } from '@core/tellent-types';
import { injectTellentAppUrlOpener } from '@core/tellent-util';

const SEAT_KIND_TO_APP_KIND: Record<SeatKind, AppKind> = {
  [SeatKind.CoreHR]: AppKind.KiwiHr,
  [SeatKind.Recruitment]: AppKind.Recruitee,
  [SeatKind.Grow]: AppKind.Javelo,
};

const FALLBACK_APP_KIND_PRIORITY: SeatKind[] = [
  SeatKind.CoreHR,
  SeatKind.Recruitment,
  SeatKind.Grow,
];

@Injectable({ providedIn: 'root' })
export class TellentClientCompanySwitchStrategy implements CompanySwitchStrategy {
  private readonly store = inject(Store);
  private readonly isNativeMobileHr = injectIsNativeMobileHr();
  public open = injectTellentAppUrlOpener();
  public appKind = injectTellentAppKind();

  private currentOrgUser = this.store.selectSignal(TellentCurrentUserSelectors.data);

  public switchCompany(orgGuid: string, _companyId?: string, fallbackApps?: SeatKind[]) {
    const kind = this.resolvePreferredAppKind(orgGuid, fallbackApps);
    writeOrgGuidToLocalStorage(this.currentOrgUser()?.guid!, orgGuid);
    if (this.isNativeMobileHr) {
      switchOrgInNativeApp(orgGuid);
    } else {
      this.open(kind, undefined, orgGuid);
    }
  }

  private resolvePreferredAppKind(orgGuid: string, fallbackApps?: SeatKind[]) {
    const member = this.currentOrgUser()?.members.find(member => member.orgGuid === orgGuid);

    return (
      member?.seats.find(seat => seat.app.kind === <unknown>this.appKind)?.app.kind ??
      member?.seats[0]?.app.kind ??
      this.resolveAppKindFromRequestedSeats(fallbackApps) ??
      AppKind.TellentAdmin
    );
  }

  private resolveAppKindFromRequestedSeats(fallbackApps?: SeatKind[]): AppKind | undefined {
    if (!fallbackApps?.length) return undefined;
    const match = FALLBACK_APP_KIND_PRIORITY.find(seat => fallbackApps.includes(seat));
    return match ? SEAT_KIND_TO_APP_KIND[match] : undefined;
  }
}
